import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Alert, Avatar, Tooltip, Divider,
  useTheme, useMediaQuery, Popover, List, ListItem, ListItemText
} from '@mui/material';
import { PersonAdd, PersonOff, Email, Edit } from '@mui/icons-material';
import { usersAPI, projectsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SlideTransition from '../../components/common/SlideTransition';
import DevBadge from '../../components/common/DevBadge';

const ROLE_LABELS = { member: 'Miembro' };

export default function TeamManagement() {
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverProjects, setPopoverProjects] = useState([]);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [confirm, setConfirm] = useState({ open: false, userId: null, action: null });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const load = async () => {
    try {
      const [usrRes, prjRes] = await Promise.all([usersAPI.getAll(), projectsAPI.getAll()]);
      setMembers(usrRes.data.filter(u => u.role === 'member'));
      setProjects(prjRes.data);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Depuración: mostrar datos en consola
  useEffect(() => {
    if (!loading) {
      console.log('Miembros cargados:', members);
      console.log('Proyectos cargados:', projects);
      members.forEach(user => {
        const userProjects = getMemberProjects(user._id);
        console.log(`Usuario: ${user.name || user.email} (${user._id}) participa en proyectos:`, userProjects.map(p => p.name));
      });
    }
  }, [loading, members, projects]);

  const handleInvite = async () => {
    setInviteError(''); setInviteSuccess(''); setInviting(true);
    try {
      await usersAPI.invite(inviteForm);
      setInviteSuccess(`Invitación enviada a ${inviteForm.email}`);
      setInviteForm({ email: '', name: '' });
      setTimeout(() => { setInviteDialog(false); setInviteSuccess(''); load(); }, 1500);
    } catch (err) {
      setInviteError(err.response?.data?.error || 'Error al enviar invitación');
    } finally { setInviting(false); }
  };

  const handleDeactivate = async (userId) => {
    try {
      await usersAPI.deactivate(userId);
      setConfirm({ open: false, userId: null }); load();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const getMemberProjects = (userId) =>
    projects.filter(p =>
      p.members?.some(m => {
        // Soporta userId como string o como objeto poblado
        if (!m.userId) return false;
        if (typeof m.userId === 'string') return m.userId === userId;
        if (typeof m.userId === 'object' && m.userId._id) return m.userId._id === userId;
        return false;
      })
    );

  const handleProjectsClick = (event, userProjects) => {
    setAnchorEl(event.currentTarget);
    setPopoverProjects(userProjects);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverProjects([]);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <DevBadge id="page:teamlead-equipo" />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Gestión de Equipo</Typography>
          <Typography variant="body2" color="text.secondary">Invita y gestiona los miembros de desarrollo de tu equipo</Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => { setInviteDialog(true); setInviteError(''); setInviteSuccess(''); }}>
          Invitar Miembro
        </Button>
      </Box>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Proyectos</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map(user => {
                const userProjects = getMemberProjects(user._id);
                return (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ width: 34, height: 34, bgcolor: '#1a1a1a', fontSize: 14 }}>
                          {(user.name || user.email)[0].toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {user.name ? `${user.name} ${user.lastName || ''}`.trim() : user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip label={ROLE_LABELS[user.role] || user.role} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {userProjects.length > 0 ? (
                        <Chip
                          label={`${userProjects.length} proyecto${userProjects.length > 1 ? 's' : ''}`}
                          color="primary"
                          clickable
                          onClick={e => handleProjectsClick(e, userProjects)}
                          size="small"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">Sin proyectos</Typography>
                      )}
                    </TableCell>
                          <Popover
                            open={Boolean(anchorEl)}
                            anchorEl={anchorEl}
                            onClose={handlePopoverClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          >
                            <Box px={2} py={1.5} minWidth={260}>
                              <Typography variant="subtitle1" fontWeight={700} color="primary.main" gutterBottom>
                                Proyectos asignados
                              </Typography>
                            </Box>
                            <Divider sx={{ mb: 1, mx: 0 }} />
                            <List dense sx={{ px: 1, pb: 1 }}>
                              {popoverProjects.length > 0 ? (
                                popoverProjects.map((p, idx) => (
                                  <React.Fragment key={p._id}>
                                    <ListItem disablePadding>
                                      <ListItemText primary={p.name} primaryTypographyProps={{ fontWeight: 500 }} />
                                    </ListItem>
                                    {idx < popoverProjects.length - 1 && <Divider component="li" sx={{ my: 0.5 }} />}
                                  </React.Fragment>
                                ))
                              ) : (
                                <ListItem>
                                  <ListItemText primary="Sin proyectos" />
                                </ListItem>
                              )}
                            </List>
                          </Popover>
                    <TableCell>
                      <Chip
                        label={user.status === 'active' ? 'Activo' : user.status === 'invited' ? 'Invitado' : 'Inactivo'}
                        size="small"
                        color={user.status === 'active' ? 'success' : user.status === 'invited' ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
                        {user.status === 'active' && (
                          <Tooltip title="Desactivar usuario">
                            <IconButton size="small" color="error" sx={{ borderRadius: '5px', bgcolor: 'rgba(211,47,47,0.08)', '&:hover': { bgcolor: 'rgba(211,47,47,0.18)' } }}
                              onClick={() => setConfirm({ open: true, userId: user._id, action: 'deactivate' })}>
                              <PersonOff fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {members.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>No hay miembros aún. Invita a tu equipo.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="xs" fullWidth fullScreen={isMobile} TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email color="primary" /> Invitar Miembro
          <DevBadge id="modal:invitar-miembro" inline />
        </DialogTitle>
        <DialogContent>
          {inviteError && <Alert severity="error" sx={{ mb: 2 }}>{inviteError}</Alert>}
          {inviteSuccess && <Alert severity="success" sx={{ mb: 2 }}>{inviteSuccess}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Email *" type="email" value={inviteForm.email}
              onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} fullWidth />
            <TextField label="Nombre (opcional)" value={inviteForm.name}
              onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))} fullWidth />
          </Box>
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            Se enviará un email con instrucciones de acceso al sistema.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInviteDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleInvite}
            disabled={inviting || !inviteForm.email || !!inviteSuccess}>
            {inviting ? 'Enviando...' : 'Enviar Invitación'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirm.open} title="Desactivar Usuario"
        message="¿Desactivar este usuario? Perderá acceso al sistema."
        confirmLabel="Desactivar"
        onConfirm={() => handleDeactivate(confirm.userId)}
        onCancel={() => setConfirm({ open: false, userId: null })}
      />
    </Box>
  );
}
